import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router';
import { Disclaimer } from './primitives.js';
import { setStatusBarBackdrop } from '@/lib/systemBars.js';
import { Icon, type IconName } from './icons.js';

const TABS = [
  { to: '/progress', label: 'Progress', icon: 'progress' },
  { to: '/capture', label: 'Session', icon: 'session' },
  { to: '/coach', label: 'Darwin', icon: 'coach' },
  // The program; the exercise library opens from it (2026-10-16).
  { to: '/program', label: 'Plan', icon: 'plan' },
  { to: '/settings', label: 'Settings', icon: 'settings' },
] as const satisfies ReadonlyArray<{ to: string; label: string; icon: IconName }>;

/**
 * The tab screens a detail screen (a report, the exercise library) is opened
 * from. The link passes it as `state.from`; the detail screen's back link and
 * the highlighted tab follow it.
 */
export type Origin = 'progress' | 'plan' | 'coach';
const ORIGIN = {
  progress: { to: '/progress', label: 'Progress' },
  plan: { to: '/program', label: 'Plan' },
  coach: { to: '/coach', label: 'Darwin' },
} as const;

/** Link state for a detail screen opened from the current one. */
export function useOrigin(): { from: Origin } {
  const { pathname } = useLocation();
  return { from: pathname.startsWith('/program') ? 'plan' : pathname.startsWith('/coach') ? 'coach' : 'progress' };
}

/** Detail screens without a tab of their own, and the tab they belong to when opened directly. */
const DETAIL_HOME: Array<[prefix: string, home: Origin]> = [
  ['/report', 'progress'],
  ['/exercises', 'plan'],
];

function tabFor(pathname: string, from: Origin | undefined): string {
  const home = DETAIL_HOME.find(([prefix]) => pathname.startsWith(prefix))?.[1];
  return home ? ORIGIN[from ?? home].to : pathname;
}

/**
 * App shell: a frame exactly one screen tall. The tab bar is pinned to the
 * bottom edge, the top inset is fixed, and only the content between them
 * scrolls — so no screen can shift the frame, leave a gap under the bar, or
 * push it off the bottom while it loads. (The iOS WebView does not scroll
 * the document at all — `scrollEnabled: false` — so an inner scroller is the
 * only scrolling there is.)
 *
 * Capture and the live session take the whole screen: the camera preview is
 * the interface, and they lay out their own insets.
 */
export function AppLayout(): ReactNode {
  const { pathname, key, state } = useLocation();
  const activeTab = tabFor(pathname, (state as { from?: Origin } | null)?.from);
  const navigationType = useNavigationType();
  const bare = pathname.startsWith('/session') || pathname.startsWith('/capture') || pathname.startsWith('/check');
  const scroller = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // The launch splash owns the status bar until it leaves (main.tsx).
    if (document.querySelector('[data-splash]')) return;
    // Camera screens set their own (light while the camera opens, then dark).
    if (!bare) void setStatusBarBackdrop('light');
  }, [bare]);

  // A new screen starts at its top, not at the previous tab's scroll offset.
  // Going back (back button, Android back) returns to where that screen was
  // left: offsets are kept per history entry. The page refetches when it
  // mounts, so the offset is reapplied each frame until the content is tall
  // enough, the user touches the screen, or 2 s pass.
  const offsets = useRef(new Map<string, number>());
  const restoring = useRef(false);
  const lastPath = useRef(pathname);
  useLayoutEffect(() => {
    const el = scroller.current;
    const samePage = lastPath.current === pathname;
    lastPath.current = pathname;
    if (!el) return;
    const target = navigationType === 'POP' ? offsets.current.get(key) : undefined;
    if (!target) {
      if (!samePage) el.scrollTo({ top: 0 });
      return;
    }
    let frame = 0;
    const stop = (): void => {
      restoring.current = false;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      el.removeEventListener('touchstart', stop);
      el.removeEventListener('wheel', stop);
    };
    const apply = (): void => {
      el.scrollTop = target;
      if (el.scrollTop >= target - 1) stop();
      else frame = window.requestAnimationFrame(apply);
    };
    restoring.current = true;
    el.addEventListener('touchstart', stop, { passive: true });
    el.addEventListener('wheel', stop, { passive: true });
    const timer = window.setTimeout(stop, 2000);
    apply();
    return stop;
  }, [key, pathname, navigationType]);

  if (bare) return <Outlet />;

  return (
    <div
      className="mx-auto flex h-dvh w-full max-w-screen-sm flex-col overflow-hidden"
      // The status-bar inset belongs to the frame, not the scroller: inside it,
      // scrolled content slid up under the clock and battery.
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/*
        No app-name bar: each screen names itself in its own PageHeader. The
        medical disclaimer LEGAL §2 requires closes every screen's content.
      */}
      <main
        ref={scroller}
        onScroll={(event) => {
          if (!restoring.current) offsets.current.set(key, event.currentTarget.scrollTop);
        }}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4"
        style={{ paddingTop: '1rem' }}
      >
        <Outlet />
        <div className="pt-6 pb-4">
          <Disclaimer />
        </div>
      </main>
      <nav
        aria-label="Main"
        className="grid shrink-0 grid-cols-5 border-t border-line bg-card"
        // The bar's own background runs under the home indicator; the tabs
        // sit above it.
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={`flex h-14 flex-col items-center justify-center gap-1 text-[11px] font-medium ${
              activeTab.startsWith(tab.to) ? 'text-ink' : 'text-muted'
            }`}
          >
            <Icon name={tab.icon} size={22} />
            {tab.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * Back to the list the screen was opened from, at the place it was left
 * (offsets are kept per history entry above). Reached any other way (straight
 * after a session, a reminder, a shared link) there is no list behind it, so
 * the link goes to `home` instead; history back there could reopen the camera.
 */
export function BackLink({ home }: { home: Origin }): ReactNode {
  const navigate = useNavigate();
  const from = (useLocation().state as { from?: Origin } | null)?.from;
  return (
    <button
      type="button"
      onClick={() => void (from ? navigate(-1) : navigate(ORIGIN[home].to))}
      className="-ml-1.5 flex items-center gap-0.5 py-1 text-sm font-semibold text-muted"
    >
      <Icon name="back" size={18} />
      {ORIGIN[from ?? home].label}
    </button>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold tracking-tight">{title}</h1>
        {subtitle ? <p className="mt-0.5 text-sm text-muted">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}
